import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, Phone, MapPin, Linkedin, Twitter, Facebook, Youtube, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { navLinks, companyInfo, brand, contactPage } from '../mock/mock';
import { useToast } from '../hooks/use-toast';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const { toast } = useToast();

  const onSubscribe = (e) => {
    e.preventDefault();
    if (!email.includes('@')) {
      toast({ title: 'Please enter a valid email', description: 'We need a valid email address.' });
      return;
    }
    setSubscribed(true);
    toast({ title: 'Subscribed!', description: 'Thanks for subscribing to DC10G updates.' });
    setEmail('');
    setTimeout(() => setSubscribed(false), 3500);
  };

  return (
    <footer id="contact" className="relative bg-[#030509] text-slate-300 pt-20 overflow-hidden border-t border-white/5">
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute top-0 left-1/3 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-purple-500/10 blur-3xl" />

      <div className="container-x px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="rounded-3xl glass-strong border border-cyan-400/25 p-8 md:p-12 mb-16 grid md:grid-cols-2 gap-8 items-center"
        >
          <div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white">Subscribe to <span className="gradient-text">Newsletter</span></h3>
            <p className="mt-2 text-slate-300">Get the latest updates on cloud infrastructure, offers and industry insights.</p>
          </div>
          <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full pl-12 pr-4 py-3 rounded-lg bg-slate-900/70 border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500" />
            </div>
            <button type="submit" className="px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold inline-flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-cyan-500/30 hover:-translate-y-0.5 transition-all">
              {subscribed ? <><CheckCircle2 size={18} /> Subscribed</> : <>Subscribe <Send size={16} /></>}
            </button>
          </form>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12">
          <div>
            <div className="bg-white rounded-xl p-3 inline-block mb-4">
              <img src={brand.logo} alt="DC10G" className="h-10 w-auto object-contain" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">Enterprise-grade datacenter and hosting solutions since 2006. Powering businesses with Tier III infrastructure worldwide.</p>
            <div className="mt-5 flex gap-3">
              {[Linkedin, Twitter, Facebook, Youtube].map((Icon, i) => (
                <a key={i} href="#" className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 hover:bg-cyan-500 hover:border-cyan-500 flex items-center justify-center transition-colors">
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {navLinks.map((l) => (
                <li key={l.label}>
                  {l.href.startsWith('/') && !l.href.startsWith('/#') ? (
                    <Link to={l.href} className="hover:text-cyan-400 transition-colors">{l.label}</Link>
                  ) : (
                    <a href={l.href} className="hover:text-cyan-400 transition-colors">{l.label}</a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Services</h4>
            <ul className="space-y-2 text-sm">
              {['Cloud Services', 'Dedicated Server', 'VPS Hosting', 'Backup as a Service', 'Zimbra Hosting', 'Cybersecurity', 'Managed Services'].map((s) => (
                <li key={s}><a href="/#services" className="hover:text-cyan-400 transition-colors">{s}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <Phone size={16} className="text-cyan-400 mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Sales</div>
                  <a href={`tel:${companyInfo.salesPhone}`} className="hover:text-cyan-400">{companyInfo.salesPhone}</a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={16} className="text-cyan-400 mt-0.5" />
                <div>
                  <div className="text-slate-500 text-xs">Support</div>
                  <a href={`tel:${companyInfo.supportPhone}`} className="hover:text-cyan-400">{companyInfo.supportPhone}</a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={16} className="text-cyan-400 mt-0.5" />
                <a href={`mailto:${companyInfo.email}`} className="hover:text-cyan-400">{companyInfo.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-cyan-400 mt-0.5" />
                <span className="text-xs leading-relaxed">{contactPage.corporateOffice.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 py-6 flex flex-col md:flex-row justify-between gap-3 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} DC10G · SST Infotech (India) Pvt. Ltd. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-cyan-400">Privacy Policy</a>
            <a href="#" className="hover:text-cyan-400">Terms of Service</a>
            <a href="#" className="hover:text-cyan-400">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
